// Typed API client — all requests go through here
// Auth tokens injected from authStore; 401 responses trigger silent token refresh

const API_URL = import.meta.env['VITE_API_URL'] ?? 'http://localhost:8787'
const REFRESH_TOKEN_KEY = 'sm-refresh-token'
const IMPERSONATION_TOKEN_KEY = 'sm-impersonation-token'

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ── Token state ───────────────────────────────────────────────────────────────

let _getToken: (() => string | null) | null = null
let _setToken: ((token: string) => void) | null = null
let _clearAuth: (() => void) | null = null
let _getIsImpersonating: (() => boolean) | null = null
let _exitImpersonation: (() => void) | null = null

// Single in-flight refresh promise — prevents concurrent refresh storms
let _refreshPromise: Promise<string | null> | null = null

export function setTokenGetter(fn: () => string | null): void {
  _getToken = fn
}

export function setTokenSetter(fn: (token: string) => void): void {
  _setToken = fn
}

export function setClearAuth(fn: () => void): void {
  _clearAuth = fn
}

export function setImpersonationHandlers(
  isImpersonating: () => boolean,
  exitImpersonation: () => void,
): void {
  _getIsImpersonating = isImpersonating
  _exitImpersonation = exitImpersonation
}

// ── Token refresh ─────────────────────────────────────────────────────────────

async function refreshAccessToken(): Promise<string | null> {
  // Deduplicate concurrent refresh attempts
  if (_refreshPromise) return _refreshPromise

  _refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
      _clearAuth?.()
      return null
    }

    try {
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })

      if (!res.ok) {
        // Refresh token is invalid or expired — force logout
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        _clearAuth?.()
        return null
      }

      const data = (await res.json()) as { accessToken: string; refreshToken: string }
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
      _setToken?.(data.accessToken)
      return data.accessToken
    } catch {
      // Network error during refresh — don't clear auth, just return null
      return null
    } finally {
      _refreshPromise = null
    }
  })()

  return _refreshPromise
}

// ── Core request ──────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  options: RequestInit & { token?: string; apiKey?: string; _isRetry?: boolean } = {},
): Promise<T> {
  const { token, apiKey, _isRetry, ...fetchOptions } = options

  const headers = new Headers(fetchOptions.headers)
  headers.set('Content-Type', 'application/json')

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  } else if (apiKey) {
    headers.set('X-API-Key', apiKey)
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...fetchOptions,
    headers,
  })

  // ── 401 interceptor ───────────────────────────────────────────────────────
  if (res.status === 401 && !_isRetry && token) {
    // If we're impersonating, the impersonation token expired — exit cleanly
    // without touching the admin's real session
    if (_getIsImpersonating?.()) {
      localStorage.removeItem(IMPERSONATION_TOKEN_KEY)
      _exitImpersonation?.()
      throw new ApiError(401, 'IMPERSONATION_EXPIRED', 'Impersonation session has expired')
    }

    // Normal token refresh
    const newToken = await refreshAccessToken()
    if (newToken) {
      return request<T>(path, { ...options, token: newToken, _isRetry: true })
    }
    // Refresh failed — fall through to throw
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: { code: 'UNKNOWN', message: res.statusText } }))
    const err = (body as { error: { code: string; message: string; details?: Record<string, unknown> } }).error
    throw new ApiError(res.status, err.code, err.message, err.details)
  }

  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

// ── Authed helper — prefers impersonation token when active ───────────────────

function authed(options: RequestInit = {}): RequestInit & { token?: string } {
  // When impersonating, use the impersonation token for all non-admin requests
  if (_getIsImpersonating?.()) {
    const impToken = localStorage.getItem(IMPERSONATION_TOKEN_KEY)
    if (impToken) return { ...options, token: impToken }
  }
  const token = _getToken?.() ?? null
  return token ? { ...options, token } : options
}

// Admin requests always use the real admin token (never the impersonation token)
function authedAdmin(options: RequestInit = {}): RequestInit & { token?: string } {
  const token = _getToken?.() ?? null
  return token ? { ...options, token } : options
}

// ── Public API ────────────────────────────────────────────────────────────────

export const api = {
  get: <T>(path: string) => request<T>(path, authed({ method: 'GET' })),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, authed({ method: 'POST', body: JSON.stringify(body) })),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, authed({ method: 'PATCH', body: JSON.stringify(body) })),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, authed({ method: 'PUT', body: JSON.stringify(body) })),
  delete: <T>(path: string) => request<T>(path, authed({ method: 'DELETE' })),

  // Admin requests — always use real admin token
  admin: {
    get: <T>(path: string) => request<T>(path, authedAdmin({ method: 'GET' })),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, authedAdmin({ method: 'POST', body: JSON.stringify(body) })),
    patch: <T>(path: string, body?: unknown) =>
      request<T>(path, authedAdmin({ method: 'PATCH', body: JSON.stringify(body) })),
    put: <T>(path: string, body?: unknown) =>
      request<T>(path, authedAdmin({ method: 'PUT', body: JSON.stringify(body) })),
    delete: <T>(path: string) => request<T>(path, authedAdmin({ method: 'DELETE' })),
  },

  // Public (no auth)
  public: {
    get: <T>(path: string) => request<T>(path, { method: 'GET' }),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  },
}

export { ApiError }
